import { PinnerCredits, PinnerProData } from '../interfaces/Pinners';
import apiService from './api.service';

class UserService {
  // Cache locale per evitare chiamate ridondanti
  private userProfileCache: Map<string, { data: PinnerProData; timestamp: number }> = new Map();
  private creditsCache: { data: PinnerCredits | null; timestamp: number } = { data: null, timestamp: 0 };
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

  // Ottieni il profilo dell'utente autenticato
  async getAuthProfile(forceRefresh = false): Promise<PinnerProData> {
    const cacheKey = 'auth';

    if (!forceRefresh) {
      const cached = this.userProfileCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }
    }

    const response = await apiService.get<PinnerProData>('/users/auth/profile/');

    // Salva in cache
    this.userProfileCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });

    return response.data;
  }

  // Ottieni il profilo di un utente specifico
  async getUserProfile(userId: string, forceRefresh = false): Promise<PinnerProData> {
    const cacheKey = `user_${userId}`;

    if (!forceRefresh) {
      const cached = this.userProfileCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }
    }

    const response = await apiService.get<PinnerProData>(`/users/${userId}/`);

    // Salva in cache
    this.userProfileCache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });

    return response.data;
  }

  // Aggiorna il profilo utente
  async updateProfile(data: Partial<PinnerProData>): Promise<PinnerProData> {
    const response = await apiService.patch<PinnerProData>('/users/auth/profile/', data);

    // Invalida la cache dopo l'aggiornamento
    this.userProfileCache.delete('auth');

    // Aggiorna la cache con i nuovi dati
    this.userProfileCache.set('auth', {
      data: response.data,
      timestamp: Date.now()
    });

    return response.data;
  }

  // Upload foto profilo
  async uploadProfilePicture(file: File, onProgress?: (progress: number) => void): Promise<PinnerProData> {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const response = await apiService.patch<PinnerProData>('/users/auth/profile/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    // Invalida la cache
    this.userProfileCache.delete('auth');

    return response.data;
  }

  // Elimina foto profilo
  async deleteProfilePicture(): Promise<void> {
    await apiService.delete('/users/auth/profile/picture/');

    // Invalida la cache
    this.userProfileCache.delete('auth');
  }

  // Ottieni i crediti dell'utente
  async getCredits(forceRefresh = false): Promise<PinnerCredits> {
    if (!forceRefresh && this.creditsCache.data &&
      Date.now() - this.creditsCache.timestamp < this.CACHE_DURATION) {
      return this.creditsCache.data;
    }

    const response = await apiService.get<PinnerCredits>('/billing/credits/');

    // Salva in cache
    this.creditsCache = {
      data: response.data,
      timestamp: Date.now()
    };

    return response.data;
  }

  // Cerca utenti (Pinners)
  async searchUsers(params: {
    person?: string;
    place?: string;
    limit?: number;
    offset?: number;
    language?: string[];
  }): Promise<{ results: PinnerProData[]; count: number; next: string | null }> {
    const response = await apiService.get('/users/', { params });
    return response.data;
  }

  // Invalida tutta la cache
  clearCache(): void {
    this.userProfileCache.clear();
    this.creditsCache = { data: null, timestamp: 0 };
  }

  // Invalida cache specifica
  invalidateUserCache(userId?: string): void {
    if (userId) {
      this.userProfileCache.delete(`user_${userId}`);
    } else {
      this.userProfileCache.delete('auth');
    }
  }

  invalidateCreditsCache(): void {
    this.creditsCache = { data: null, timestamp: 0 };
  }
}

// Esporta istanza singleton
const userService = new UserService();
export default userService;
